import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { FormProvider, useForm } from 'react-hook-form'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { rem } from 'polished'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import { POSITIVE_FEEDBACK, NEGATIVE_FEEDBACK } from '../../constants/feedbackType'
import RHFTextArea from 'components/Common/Form/Input/RHFTextArea'
import Group from 'components/utils/Group'

const ErrorLabel = styled(Caption)`
  background: ${props => props.theme.palette.messaging.critical.lightBackground};
  border: 1px solid ${props => props.theme.palette.messaging.critical.normalForeground};
  padding: ${rem(8)} ${rem(8)};
  margin-top: ${rem(8)};
`

const StyledSubtitleBodyText = styled(BodyText)`
  margin-bottom: ${rem(12)};
`

const LESA_DIDNT_UNDERSTAND_QUESTION = 'LESA didn’t understand my question(s)'
const THE_ANSWERS_I_GOT_WERE_INACCURATE = 'The answers I got were inaccurate'
const REPLIES_FROM_LESA_TOOK_TOO_LONG = 'Replies from LESA took too long'
const OTHER = 'I had a different issue (please provide details below)'

export type Issues =
  | typeof LESA_DIDNT_UNDERSTAND_QUESTION
  | typeof THE_ANSWERS_I_GOT_WERE_INACCURATE
  | typeof REPLIES_FROM_LESA_TOOK_TOO_LONG
  | typeof OTHER

type FeedbackArgs = {
  issues: Array<Issues>
  message: string
}

interface FeedbackModalProps {
  sentiment?: typeof POSITIVE_FEEDBACK | typeof NEGATIVE_FEEDBACK
}

function FeedbackModal({ sentiment }: FeedbackModalProps) {
  const intl = useIntl()
  const modalContext = useModalElementContext<undefined | any>()

  const methods = useForm<FeedbackArgs>({
    mode: 'onBlur',
    shouldFocusError: true,
    reValidateMode: 'onBlur',
    defaultValues: {
      issues: [],
      message: '',
    },
  })

  const { handleSubmit, register, watch, formState: { errors } } = methods

  const issues = watch('issues')

  const onSubmitFeedback = useCallback((feedback: FeedbackArgs) => {
    modalContext.resolve(feedback)
  }, [modalContext])

  const onFooterAction = useCallback(() => {
    modalContext.resolve(undefined)
  }, [modalContext])

  const requiredMessage =
    (issues.includes(OTHER) && intl.formatMessage({
      id: 'JenZCGKrC9JhXfVX6NAT',
      defaultMessage: 'Please describe the issue you encountered with LESA Support Assistant',
      description: 'Validation message requiring details when "other issue" is selected in feedback',
    })) ||
    (sentiment === POSITIVE_FEEDBACK && intl.formatMessage({
      id: 'c9Gm1kxzN5BtWP_OUBZE',
      defaultMessage: 'Please describe your experience with our support assistant so we can improve!',
      description: 'Validation message requiring details for positive feedback',
    })) ||
    false

  const reportIssueFlow = !sentiment || sentiment === NEGATIVE_FEEDBACK

  const selectAtLeastOneIssue = intl.formatMessage({
    id: 'puoynbXG-mdFInEWdRVZ',
    defaultMessage: 'Please select at least one issue',
    description: 'Validation message for feedback checkboxes requiring at least one selection',
  })

  const modalTitle = sentiment
    ? intl.formatMessage({
        id: 'hV70oOphAzlKGJpnWLza',
        defaultMessage: 'Provide additional feedback',
        description: 'Title for the feedback modal when user has already given sentiment',
      })
    : intl.formatMessage({
        id: '_BNIrAht4Hh2yVXb114j',
        defaultMessage: 'Report an issue',
        description: 'Heading title for the report issue modal in support chat',
      })

  const secondaryActionText = !sentiment ? intl.formatMessage({
    id: 'Ln36hYQLfDTmQSsBFoCb',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel feedback submission in the cancellation flow',
  }) : undefined

  const textareaLabel = reportIssueFlow
    ? intl.formatMessage({
        id: 'GrLEZQ9cbJLS88yWuU2r',
        defaultMessage: 'Do you have any suggestions or comments to improve your experience with LESA?',
        description: 'Form field label for feedback textarea in report issue flow',
      })
    : intl.formatMessage({
        id: 'qzV6bBm6RES5FM1LBsVQ',
        defaultMessage: 'What do you feel about this chat?',
        description: 'Label for feedback textarea in positive feedback flow',
      })

  return <Modal
    title={modalTitle}
    primaryActionText={intl.formatMessage({
      id: '3_IsmtYrs8uZxj3Frc2l',
      defaultMessage: 'Submit feedback',
      description: 'Button text to submit feedback in support chat modal',
    })}
    onPrimaryActionClick={handleSubmit(onSubmitFeedback)}
    secondaryActionText={secondaryActionText}
    onSecondaryActionClick={!sentiment ? onFooterAction : undefined}
    size="L"
  >
    <StyledSubtitleBodyText variant="large">
      {reportIssueFlow && intl.formatMessage({
        id: 'YpnBvfbIS68gnp2qqHa-',
        defaultMessage: 'Which aspects of LESA did you find challenging?',
        description: 'Subtitle text for report issue feedback flow in a modal dialog',
      })}
      {sentiment === POSITIVE_FEEDBACK && intl.formatMessage({
        id: '2Ioyzme2oe6YghnB2nfT',
        defaultMessage: 'We appreciate your efforts and valuable feedback, which will greatly contribute to improving the overall user experience.',
        description: 'Subtitle text shown for positive feedback flow in a modal dialog',
      })}
    </StyledSubtitleBodyText>
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitFeedback)}>
        <VerticalSpacer gap={16}>
          {reportIssueFlow && <Group direction="vertical" gap={32}>
            <div>
              <CheckboxInput {...register('issues', { required: !issues.length ? selectAtLeastOneIssue : false })} required={!issues.length} value={LESA_DIDNT_UNDERSTAND_QUESTION} size="large">
                {intl.formatMessage({
                  id: 'gAJXg-turW1qcT1lKfim',
                  defaultMessage: 'LESA didn’t understand my question(s)',
                  description: 'Feedback checkbox option: LESA did not understand the question',
                })}
              </CheckboxInput>
              <CheckboxInput {...register('issues', { required: !issues.length ? selectAtLeastOneIssue : false })} required={!issues.length} value={THE_ANSWERS_I_GOT_WERE_INACCURATE} size="large">
                {intl.formatMessage({
                  id: '6Pjp1jE9IPd26_NhiVlB',
                  defaultMessage: 'The answers I got were inaccurate',
                  description: 'Feedback checkbox option: answers were inaccurate in a modal dialog',
                })}
              </CheckboxInput>
              <CheckboxInput {...register('issues', { required: !issues.length ? selectAtLeastOneIssue : false })} required={!issues.length} value={REPLIES_FROM_LESA_TOOK_TOO_LONG} size="large">
                {intl.formatMessage({
                  id: 'dNGxdXoU4UmJHnP5_RH0',
                  defaultMessage: 'Replies from LESA took too long',
                  description: 'Feedback checkbox option: replies were too slow in a modal dialog',
                })}
              </CheckboxInput>
              <CheckboxInput {...register('issues', { required: !issues.length ? selectAtLeastOneIssue : false })} required={!issues.length} value={OTHER} size="large">
                {intl.formatMessage({
                  id: '-jeH7zP2B7V0bkrZG4cK',
                  defaultMessage: 'I had a different issue (please provide details below)',
                  description: 'Feedback checkbox option: other issue with details prompt',
                })}
              </CheckboxInput>
            </div>
            {errors.issues && <ErrorLabel variant="medium">
              {intl.formatMessage({
                id: '52YwiH2mRRzPDsa57Vjf',
                defaultMessage: 'Please choose at least one issue',
                description: 'Error label shown when no feedback issue checkbox is selected',
              })}
            </ErrorLabel>}
          </Group>}
          <RHFTextArea
            {...register('message', {
              required: requiredMessage,
            })}
            required={!!requiredMessage}
            label={textareaLabel}
            placeholder={intl.formatMessage({
              id: 'pt0z2O0M2wTRzE8DGp3o',
              defaultMessage: 'Enter your feedback',
              description: 'Placeholder text for feedback textarea in a modal dialog',
            })}
            resize="vertical"
          />
        </VerticalSpacer>
      </form>
    </FormProvider>
  </Modal>
}

export default FeedbackModal
